import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight, Zap, Sun, Activity, ShieldCheck, Award, Clock, Handshake, Plus, X } from 'lucide-react';
import { STATS, FOCUS_AREAS, WHY_TES, SOLUTIONS, COMPANY } from '../data/content';
import SolarSurveyModal from '../components/SolarSurveyModal';
import EnergyAuditModal from '../components/EnergyAuditModal';
import PerformanceCarousel from '../components/PerformanceCarousel';
import { PERFORMANCE_TILES } from '../data/performanceTiles';
import CountUp from '../components/CountUp';
import { openExternal } from '../lib/openExternal';

const SOL_ICONS = [Activity, Sun, Zap];

export default function Home() {
  const [videoStarted, setVideoStarted] = useState(false);
  const [solarOpen, setSolarOpen] = useState(false);
  const [energyOpen, setEnergyOpen] = useState(false);
  const [whyOpen, setWhyOpen] = useState(null); // selected WHY_TES entry for modal
  const videoRef = useRef(null);

  // Lock body scroll while the WHY popup is open
  useEffect(() => {
    if (whyOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [whyOpen]);

  // After the video ends, hold the last frame for 3 seconds, then restart from the beginning.
  // We don't use the native `loop` attribute so the `onEnded` event actually fires.
  const handleVideoEnded = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    setTimeout(() => {
      if (!videoRef.current) return;
      videoRef.current.currentTime = 0;
      const p = videoRef.current.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }, 3000);
  };

  return (
    <main>
      {/* HERO with looping video background — full viewport height */}
      <section className="relative overflow-hidden h-screen min-h-[640px]" data-testid="hero-section" data-nav-theme="dark">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          preload="auto"
          poster="/video/hero-poster.jpg"
          onEnded={handleVideoEnded}
          onPlaying={() => setVideoStarted(true)}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-video"
          style={{willChange:'auto'}}
        >
          <source src="/video/hero.mp4" type="video/mp4" />
        </video>
        {/* Poster overlay — fades out the moment playback begins so the open is seamless. */}
        <img
          src="/video/hero-poster.jpg"
          alt=""
          aria-hidden="true"
          loading="eager"
          decoding="sync"
          fetchPriority="high"
          style={{ transitionProperty: 'opacity', transitionDuration: '400ms', transitionTimingFunction: 'cubic-bezier(0.22, 0.61, 0.36, 1)' }}
          className={`absolute inset-0 w-full h-full object-cover pointer-events-none ${videoStarted ? 'opacity-0' : 'opacity-100'}`}
        />
        {/* Subtle dark overlay — keeps the footage close to source (≤10% darker). */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, rgba(14,22,32,0.10), rgba(14,22,32,0.06) 50%, rgba(14,22,32,0.10))' }}
        />
        <div className="absolute inset-0 noise opacity-20 pointer-events-none"/>
        <div className="relative h-full px-6 md:px-10 lg:px-14 max-w-[1500px] mx-auto flex flex-col justify-center pt-[84px]">
          <div className="max-w-4xl">
            <div className="text-[12px] uppercase tracking-[0.28em] text-[var(--orange)] font-semibold mb-5 reveal">EPC Solutions</div>
            <h1 className="font-display font-bold text-[clamp(42px,6.5vw,96px)] leading-[1.02] tracking-tight reveal" style={{color:'#fff', textShadow:'0 2px 24px rgba(0,0,0,0.35)'}}>
              Affordable, Clean & Efficient
              <br/>
              <span className="font-serif-it text-[var(--orange)]">Energy Solutions</span>
            </h1>
            <p className="mt-7 text-[17px] md:text-[19px] text-white font-medium max-w-2xl leading-relaxed reveal" style={{ textShadow:'0 1px 16px rgba(0,0,0,0.45)', transitionDelay: '280ms' }}>
              End-to-end Electrical & Solar EPC services delivered with a human-centred design approach.
            </p>
          </div>
        </div>
      </section>

      {/* Marquee strip — sits cleanly below the video */}
      <div className="relative border-y border-[var(--line)] bg-[var(--cream)] py-5 overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap">
          {[...FOCUS_AREAS, ...FOCUS_AREAS, ...FOCUS_AREAS].map((f,i) => (
            <span key={i} className="inline-flex items-center gap-4 px-6 text-[14px] uppercase tracking-[0.18em] text-[var(--ink)] font-medium">
              {f}
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--orange)]"/>
            </span>
          ))}
        </div>
      </div>

      {/* ABOUT BLOCK */}
      <section className="px-6 md:px-10 lg:px-14 py-20 lg:py-32 bg-[var(--cream)]">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5 reveal">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-4">About</div>
            <h2 className="font-display text-[clamp(34px,4.3vw,62px)] leading-[1.04] font-bold tracking-tight">
              Decades of expertise in
              <br/>
              <span className="font-serif-it text-[var(--orange)]">Electrical EPC Solutions</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[16px] text-[var(--text-mid)] leading-[1.8] reveal">
            <p>M/s <b className="text-[var(--ink)]">TRICOLITE ENERGY SOLUTIONS</b> Private Limited was founded in 2012 as the EPC solutions arm of its parent organisation M/s Tricolite Electrical Industries Limited — a family-run manufacturing business of low-voltage and medium-voltage switchboards since 1989, and a part of the Trilok Chand Group which has been in the electrical business since the 1920s.</p>
            <p>Tricolite Energy Solutions (TES) is focused on providing affordable, clean and efficient energy solutions with a human-centred design approach. TES is presently a <b className="text-[var(--ink)]">720 MINR+</b> technology-oriented company with a strong focus on innovation.</p>
            <p>We have in-house capabilities for engineering, procurement and construction of world-class electrical contracts backed by the latest computer-aided software and design tools. Since October 2012, we have been delivering total-quality EPC projects following a process-driven approach. We are implementing TQM and are an <b className="text-[var(--ink)]">ISO 9001, 14001 and 45001 certified</b> company.</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-6">
              {STATS.map((s,i) => (
                <div key={i} className="reveal" style={{ transitionDelay: `${i*70}ms` }}>
                  <div className="font-display font-bold text-[34px] md:text-[42px] leading-none flex items-baseline gap-1">
                    <CountUp end={s.value} />
                    <span className="text-[var(--orange)] text-[16px] font-semibold">{s.suffix}</span>
                  </div>
                  <div className="text-[12px] uppercase tracking-wider text-[var(--text-light)] mt-3 leading-snug">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SOLUTIONS — dark section */}
      <section className="px-6 md:px-10 lg:px-14 py-20 lg:py-32 bg-[var(--ink)] text-white relative overflow-hidden" data-nav-theme="dark">
        <div className="absolute inset-0 grid-bg-dark opacity-40 pointer-events-none"/>
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[var(--orange)]/10 blur-3xl pointer-events-none"/>
        <div className="relative max-w-[1500px] mx-auto">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14 reveal">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-4">Our Solutions</div>
              <h2 className="font-display text-[clamp(34px,4.3vw,62px)] leading-[1.04] font-bold tracking-tight max-w-3xl">
                Affordable, Clean & Efficient
                <br/>
                <span className="font-serif-it text-[var(--orange)]">A-Class Electrical & Solar</span> EPC Solutions
              </h2>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-5">
            {SOLUTIONS.map((s,i) => {
              const Ic = SOL_ICONS[i];
              return (
                <article key={i} className="group bg-white/[0.04] border border-white/10 rounded-2xl p-8 hover:bg-white/[0.07] hover-lift reveal flex flex-col" style={{ transitionDelay: `${i*80}ms` }}>
                  <div className="flex items-start justify-between mb-8">
                    <div className="w-14 h-14 rounded-xl bg-[var(--orange)]/15 flex items-center justify-center group-hover:bg-[var(--orange)] transition-colors">
                      <Ic className="w-7 h-7 text-[var(--orange)] group-hover:text-white transition-colors" strokeWidth={2}/>
                    </div>
                    <span className="font-display font-bold text-white/30">{String(i+1).padStart(2,'0')}</span>
                  </div>
                  <h3 className="font-display font-bold text-[24px] leading-tight mb-5" style={{color:'#fff'}}>{s.title}</h3>
                  <ul className="space-y-3 mb-7 flex-grow">
                    {s.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-3 text-[14px] text-white/65">
                        <Plus className="w-4 h-4 text-[var(--orange)] mt-0.5 shrink-0"/>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto">
                    {i === 1 && (
                      <button onClick={() => setSolarOpen(true)} className="mb-5 w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-[var(--orange)]/15 border border-[var(--orange)]/40 text-[var(--orange)] font-semibold text-[14px] hover:bg-[var(--orange)] hover:text-white transition-all" data-testid="home-request-survey-btn">
                        <Sun className="w-4 h-4"/> Request Solar Survey
                      </button>
                    )}
                    {i === 2 && (
                      <button onClick={() => setEnergyOpen(true)} className="mb-5 w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-[var(--blue)]/15 border border-[var(--blue)]/40 text-[var(--blue-soft)] font-semibold text-[14px] hover:bg-[var(--blue)] hover:text-white transition-all" data-testid="home-request-audit-btn">
                        <Activity className="w-4 h-4"/> Request Energy Audit
                      </button>
                    )}
                    <Link to="/services" className="inline-flex items-center gap-2 text-[13px] uppercase tracking-wider font-semibold text-[var(--orange)] link-u">
                      Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform"/>
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY TES — light section */}
      <section className="px-6 md:px-10 lg:px-14 py-20 lg:py-32 bg-[var(--cream)] relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-25 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[var(--orange)]/8 blur-3xl pointer-events-none" />

        <div className="relative max-w-[1500px] mx-auto">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-16 reveal">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-4">Why Choose Us</div>
              <h2 className="font-display text-[clamp(34px,4.3vw,62px)] leading-[1.04] font-bold tracking-tight">
                Why <span className="font-serif-it text-[var(--orange)]">Tricolite Energy</span>
                <br/>Solutions?
              </h2>
            </div>
            <Link to="/services#process" className="inline-flex items-center gap-2 text-[var(--text-mid)] hover:text-[var(--orange)] transition-colors" data-testid="see-full-process-link">
              See full process <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>

          <div className="grid lg:grid-cols-3 gap-5">
            {WHY_TES.map((w,i) => (
              <article key={i} className="group relative bg-white border border-[var(--line)] rounded-2xl p-8 hover:border-[var(--orange)]/40 hover-lift reveal" style={{ transitionDelay: `${i*80}ms` }}>
                <div className="flex items-center justify-between mb-8">
                  <span className="font-display font-bold text-[14px] text-[var(--orange)] tracking-wider">{w.num}</span>
                  <div className="w-10 h-10 rounded-full border border-[var(--line)] flex items-center justify-center group-hover:bg-[var(--orange)] group-hover:border-[var(--orange)] transition-colors">
                    <ArrowUpRight className="w-4 h-4 text-[var(--text-mid)] group-hover:text-white transition-colors"/>
                  </div>
                </div>
                <h3 className="font-display font-bold text-[22px] leading-tight mb-4">{w.title}</h3>
                <p className="text-[14px] leading-relaxed text-[var(--text-mid)] line-clamp-[8]">{w.body}</p>
                <button type="button" onClick={() => setWhyOpen(w)} className="inline-flex items-center gap-2 mt-6 text-[13px] uppercase tracking-wider text-[var(--orange)] font-semibold link-u hover:text-[var(--orange-soft)] transition-colors" data-testid={`why-read-more-${i}`}>Read More</button>
              </article>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-12">
            {[
              { icon: Award, t: 'ISO Certified', s: '9001 / 14001 / 45001' },
              { icon: ShieldCheck, t: 'Safety First', s: 'TQM driven culture' },
              { icon: Clock, t: 'On-Time Delivery', s: 'Process driven approach' },
              { icon: Handshake, t: 'Trusted Clients', s: 'Long-standing relationships' },
            ].map((c,i) => (
              <div key={i} className="flex items-center gap-4 p-5 rounded-xl border border-[var(--line)] bg-white reveal" style={{ transitionDelay: `${i*70}ms` }}>
                <c.icon className="w-6 h-6 text-[var(--orange)] shrink-0"/>
                <div>
                  <div className="font-semibold text-[14px]">{c.t}</div>
                  <div className="text-[12px] text-[var(--text-mid)]">{c.s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PERFORMANCE APPRECIATION — editorial 2-column reviews mirroring the About page treatment */}
      <section className="px-6 md:px-10 lg:px-14 py-20 lg:py-32 bg-[var(--cream-deep)]">
        <div className="max-w-[1500px] mx-auto">
          <div className="mb-14 reveal" data-testid="performance-appreciation-heading">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-4">Client Reviews</div>
            <h2 className="font-display text-[clamp(34px,4.3vw,62px)] leading-[1.04] font-bold tracking-tight">
              Performance <span className="font-serif-it text-[var(--orange)]">Appreciation.</span>
            </h2>
          </div>

          <div data-testid="home-performance-carousel-wrap">
            <PerformanceCarousel tiles={PERFORMANCE_TILES} variant="light" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-10 lg:px-14 pb-20 bg-[var(--cream-deep)]">
        <div className="max-w-[1500px] mx-auto relative overflow-hidden rounded-3xl bg-[var(--ink)] text-white p-10 md:p-16">
          <div className="absolute inset-0 grid-bg-dark opacity-50"/>
          <div className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full bg-[var(--orange)]/15 blur-3xl"/>
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-4">Feel Free to Contact Us</div>
              <h2 className="font-display text-[clamp(30px,4vw,54px)] leading-[1.05] font-bold">
                Ready to{' '}
                <span className="font-serif-it text-[var(--orange)]">energize your next project?</span>
              </h2>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[var(--orange)] hover:bg-[var(--orange-soft)] text-white px-7 py-4 rounded-full font-medium transition-colors">Get In Touch <ArrowUpRight className="w-4 h-4"/></Link>
              <a href={COMPANY.whatsapp} onClick={openExternal(COMPANY.whatsapp)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-white/20 hover:border-white px-7 py-4 rounded-full font-medium transition-colors" data-testid="home-whatsapp-btn">WhatsApp Us</a>
            </div>
          </div>
        </div>
      </section>
      <SolarSurveyModal open={solarOpen} onClose={() => setSolarOpen(false)}/>
      <EnergyAuditModal open={energyOpen} onClose={() => setEnergyOpen(false)}/>

      {/* WHY TES — read more popup */}
      {whyOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="why-modal-title"
          onClick={() => setWhyOpen(null)}
          data-testid="why-modal"
        >
          <div className="absolute inset-0 bg-[var(--ink)]/70 backdrop-blur-sm" />
          <div
            className="relative bg-white rounded-3xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45)] max-w-[640px] w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4 p-7 md:p-8 border-b border-[var(--line)]">
              <span className="font-display font-bold text-[14px] text-[var(--orange)] tracking-wider mt-1.5 shrink-0">{whyOpen.num}</span>
              <h3 id="why-modal-title" className="font-display font-bold text-[22px] md:text-[26px] leading-tight text-[var(--ink)] flex-1">
                {whyOpen.title}
              </h3>
              <button
                type="button"
                onClick={() => setWhyOpen(null)}
                aria-label="Close"
                className="w-9 h-9 rounded-full border border-[var(--line)] flex items-center justify-center hover:border-[var(--orange)] hover:text-[var(--orange)] transition-colors shrink-0"
                data-testid="why-modal-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-7 md:p-8 overflow-y-auto">
              <p className="text-[15px] leading-[1.85] text-[var(--text-mid)] whitespace-pre-line">
                {whyOpen.body}
              </p>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
